import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentFactoryResolver,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewChild,
    ViewContainerRef
} from "@angular/core";
import {Table} from "../../../interfaces/table.interface";
import {HelpersService} from "../../../services/helpers.service";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {takeUntil} from "rxjs/operators";
import {Api3Service} from "../../../services/api3.service";
import ITableApi = Table.ITableApi;
import {Api, ApiService} from "../../../services/api.service";
import {data} from "jquery";

@Component({
    selector: "common-table-cell",
    template: `
        <div *ngIf="showPreloader" class="cell-preloader">
            <mat-icon>hdr_strong</mat-icon>
            <mat-icon>hdr_weak</mat-icon>
        </div>
        <div *ngIf="!showPreloader" (click)="onClick()">
            <ng-template [ngIf]="col.factory" #componentContainer></ng-template>

            <ng-template [ngIf]="!col.factory">
                <div [innerHTML]="colValue"></div>
                <!--                <ng-template #renderElse>{{ get_value(rowData, col.data) }}</ng-template>-->
            </ng-template>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableCellComponent implements AfterViewInit, OnDestroy, OnChanges {

    private destroy$: EventEmitter<boolean> = new EventEmitter<boolean>();

    @Input()
    public rowData: any;

    @Input()
    public col: Table.ICol;

    @ViewChild("componentContainer", {read: ViewContainerRef, static: false})
    public componentContainer: ViewContainerRef;

    public colValue: SafeHtml = "";

    public showPreloader: boolean = false;

    public constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private cfr: ComponentFactoryResolver,
        private sanitizer: DomSanitizer,
        private apiService: ApiService,
        private api3Service: Api3Service,
    ) {
    }

    private async renderCol(): Promise<void> {
        if (this.col.asyncGet) {
            this.showPreloader = true;
            this.changeDetectorRef.markForCheck();

            const api: ITableApi = this.col.asyncGet(this.rowData);
            let res: Api.IResponse;

            if (api.version == 3) {
                res = await this.api3Service.get(api.url.join("/"), api.query);
            } else {
                res = await this.apiService.request(Api.EMethod.Get, api.url, api.query);
            }

            this.showPreloader = false;
            this.changeDetectorRef.markForCheck();

            if (res.data) {
                this.rowData[this.col.data] = res.data;
            }
        }


        if (this.col.render) {
            this.colValue = this.trustHtml(this.col.render(this.rowData));
        } else {
            this.colValue = this.get_value(this.rowData, this.col.data);
        }
        this.changeDetectorRef.markForCheck();
    }

    private renderComponent(): void {
        this.componentContainer.clear();
        const componentFactory: any = this.cfr.resolveComponentFactory(this.col.factory.component);

        const colComponentRef: any = this.componentContainer.createComponent(componentFactory);

        const colComponent: any = colComponentRef.instance;

        for (const propKey of Object.keys(this.col.factory.properties)) {

            const propValue = this.col.factory.properties[propKey];

            if (propValue instanceof TableCellSubscriber) {
                (colComponent[propKey] as EventEmitter<any>)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe(($event: any): void => {
                        propValue.emit(this.rowData, $event);
                    });

            } else if (propValue instanceof TableCellRowValue) {
                colComponent[propKey] = propValue.value(this.rowData);
            } else {
                colComponent[propKey] = propValue;
            }
        }

        this.changeDetectorRef.detectChanges();
    }

    private get_value(obj: any, path: string): string {
        const value: string = HelpersService.get_value(obj, path);

        if (Array.isArray(value)) {
            return value.join(", ");
        }

        return value;
    }

    public async onClick(): Promise<void> {
        if (!this.col.click) {
            return;
        }

        try {
            this.col.click(this.rowData).then((): void => {
                this.renderCol();
            });
        } catch (e) {
        }
    }


    public trustHtml(html: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    public ngAfterViewInit(): void {
        if (this.col.factory && this.componentContainer) {
            this.renderComponent();
        } else {
            this.renderCol();
        }
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        this.destroy$.next(true);
        if (changes.col) {
            this.col = changes.col.currentValue;
            if (this.col.factory && this.componentContainer) {
                this.renderComponent();
            }
            this.changeDetectorRef.markForCheck();
        }

        if (changes.rowData) {
            this.rowData = changes.rowData.currentValue;
            this.changeDetectorRef.markForCheck();
            this.renderCol();
        }
    }

}


export class TableCellSubscriber {

    private callback: (rowData: any, event?: any) => any;

    public constructor(callback: ((rowData: any, event?: any) => any)) {
        this.callback = callback;
    }

    public emit(rowData: any, event: any): void {
        this.callback(rowData, event);
    }

}

export class TableCellRowValue {

    private key: string;

    public constructor(key: string) {
        this.key = key;
    }

    public value(rowData: any): string {
        return rowData[this.key];
    }

}
